import { useCallback, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import ErrorMessage from '@/components/ErrorMessage';
import Input from '@/components/form/Input';
import Label from '@/components/form/Label';
import { updateUser } from '@/store/profile/thunks';
import { showError, showSuccess } from '@/utils';

import SectionForm from '../SectionForm';

function Contact({ title, description }) {
	const dispatch = useDispatch();
	const [errorMessage, setErrorMessage] = useState('');
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);

	const user = useSelector(state => state.profile.user);

	const schema = Yup.object({
		firstName: Yup.string().required(),
		lastName: Yup.string().required(),
		email: Yup.string().email().required(),
		phoneArea: Yup.string()
			.matches(/^\+?\d+/, {
				excludeEmptyString: true,
				message: t('validations.invalidAreaCode')
			})
			.when('phoneNumber', {
				is: phone => !!phone,
				then: currentSchema => currentSchema.required(),
				otherwise: currentSchema => currentSchema
			}),
		phoneNumber: Yup.string()
	});

	const methods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			firstName: user?.firstName || '',
			lastName: user?.lastName || '',
			email: user?.email || '',
			phoneArea: user?.phoneNumber?.split('-')[0] || '',
			phoneNumber: user?.phoneNumber?.split('-')[1] || ''
		},
		mode: 'onChange'
	});

	const onSubmit = useCallback(async function (values) {
		setLoading(true);
		dispatch(
			updateUser({
				email: values.email,
				firstName: values.firstName,
				lastName: values.lastName,
				phoneNumber: values.phoneArea + '-' + values.phoneNumber
			})
		)
			.unwrap()
			.then(() => {
				setLoading(false);
				showSuccess(t('account.toast'));
			})
			.catch(error => {
				setErrorMessage(t('errors.generic'));
				showError(error);
				setLoading(false);
			});
	}, []);

	return (
		<SectionForm
			title={title}
			description={description}
			methods={methods}
			onSubmit={onSubmit}
			loading={loading}
		>
			<div className="m-0 flex w-full flex-col items-center">
				<div className="flex w-full flex-col gap-x-5 gap-y-4 lg:gap-y-6">
					<Input
						name="firstName"
						label={`${t('account.form.name')}`}
						placeholder={`${t('account.form.placeholder.name')}`}
						size="lg"
					/>
					<Input
						name="lastName"
						label={`${t('account.form.lastname')}`}
						placeholder={`${t('account.form.placeholder.lastname')}`}
						size="lg"
					/>
					<Input
						name="email"
						label={`${t('common.email')}`}
						placeholder={`${t('account.form.placeholder.email')}`}
						size="lg"
					/>

					<div className="form-input relative w-full">
						<Label htmlFor="phone">{t('account.form.phone')}</Label>
						<div className="flex gap-4" id="phone">
							<Input
								className="w-[90px]"
								name="phoneArea"
								type="number"
								placeholder={t('account.form.placeholder.phone.area')}
								size="lg"
							/>
							<Input
								name="phoneNumber"
								type="number"
								placeholder={t('account.form.placeholder.phone.main')}
								size="lg"
							/>
						</div>
					</div>
				</div>

				{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
			</div>
		</SectionForm>
	);
}

export default Contact;
